import React, { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';

import { useFinancialVerification } from './FinancialVerificationContext';
import usePlaid from '../../../../hooks/usePlaid';
import OutlineButton from '../../../shared/buttons/OutlineButton';
import PrimaryButton from '../../../shared/buttons/PrimaryButton';

export default function BankConnection({ onUploadBankStatements }) {
  const [shouldOpenPlaid, setShouldOpenPlaid] = useState(false);
  const { fetchLinkToken, linkToken, isLoading: plaidLoading } = usePlaid();
  const { plaidConnected, plaidConnectionData, connectPlaid, disconnectPlaid } =
    useFinancialVerification();

  const onPlaidSuccess = async (publicToken, metadata) => {
    const connectionData = {
      bankName: metadata.institution?.name || 'Connected via Plaid',
      publicToken: publicToken,
    };

    connectPlaid(connectionData);
  };

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (shouldOpenPlaid && linkToken && ready) {
      open();
      setShouldOpenPlaid(false);
    }
  }, [shouldOpenPlaid, linkToken, ready, open]);

  const handleConnectWithPlaid = async () => {
    try {
      if (!linkToken) {
        await fetchLinkToken();
      }
      setShouldOpenPlaid(true);
    } catch (error) {
      console.error('Failed to connect with Plaid:', error);
    }
  };

  const handleDisconnect = () => {
    disconnectPlaid();
  };

  return (
    <div className="bg-white border border-primary-border-gray rounded-lg p-4 mb-6 flex gap-2">
      <div className="w-full flex flex-col">
        <h3 className="text-base">Link Business Bank Account with Plaid</h3>
        {plaidConnected && (
          <p className="text-xs my-3">{plaidConnectionData.bankName}</p>
        )}
        <div className="text-xs max-w-max rounded-md p-1.5 text-success bg-background-success font-medium mt-2">
          Faster Approval
        </div>
        {!plaidConnected && (
          <div className="mt-4">
            <button
              onClick={onUploadBankStatements}
              className="text-primary-blue hover:text-primary-blue-dark text-xs"
            >
              Upload bank statements instead
            </button>
          </div>
        )}
      </div>
      <div className="w-1/2 flex items-center justify-end">
        {plaidConnected ? (
          <OutlineButton
            onClick={handleDisconnect}
            buttonText="Change"
            ariaLabel="Change"
          />
        ) : (
          <PrimaryButton
            onClick={handleConnectWithPlaid}
            buttonText={plaidLoading ? 'Connecting...' : 'Connect with Plaid'}
            ariaLabel="Connect with Plaid"
            buttonColor="bg-primary-blue"
            hoverClass="hover:bg-primary-blue-hover"
            disabled={plaidLoading}
          />
        )}
      </div>
    </div>
  );
}
